import type {
  SystemStyleObject
} from '@chakra-ui/react'
import {
  Badge,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import {
  Ellipsis,
  EmptyTablePlaceholder,
  TableSkeleton,
} from 'BrokerTsLib/src/components'
import { organizationListContext } from 'brokertslib/src/context/OrganizationListContext'
import type { OrganizationUnitGridItem } from 'BrokerTsLib/src/dtos'
import { getOrganizationUnitTypeName } from 'brokertslib/src/enums'
import type {
  ColumnDefinition,
  ColumnsDefinition,
} from 'BrokerTsLib/src/interfaces/columnsDefinition'
import { useContext } from 'react'

import { TableWrapper, ValidityBadge } from '@/components'
import { OrganizationLink } from '@/components/OrganizationLink'

export const OrganizationListNameColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'name',
  name: 'Název',
  cellExpr: (item) => (
    <OrganizationLink
      sx={{ fontWeight: 'medium' }}
      ouId={item.id}
      name={item.name}
    />
  ),
}

export const OrganizationListSuperiorOrganizationColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'superiorOrganizationUnitName',
  name: 'Nadřízená organizace',
  cellExpr: (item) =>
    item.superiorOrganizationUnitId && (
      <OrganizationLink
        ouId={item.superiorOrganizationUnitId}
        name={item.superiorOrganizationUnitName}
      />
    ),
}

export const OrganizationListContactPersonColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'contactName',
  name: 'Kontaktní osoba',
  cellExpr: (item) => (
    <Ellipsis>{item.contactName}</Ellipsis>
  ),
  cellProps: { maxW: '150px' },
}
export const OrganizationListCityColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'addressCity',
  name: 'Obec',
  cellExpr: (item) => (
    <Ellipsis>{item.addressCity}</Ellipsis>
  ),
  cellProps: { maxW: '150px' },
}

export const OrganizationListDescriptionColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'description',
  name: 'Popis',
  cellExpr: (item) => (
    <Ellipsis>{item.description}</Ellipsis>
  ),
  cellProps: { maxW: '150px' },
}

export const OrganizationListValidColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'valid',
  name: '',
  cellExpr: (item) => <ValidityBadge valid={item.valid} />,
  cellProps: { p: 1 },
}

export const OrganizationListTypeColumn: ColumnDefinition<OrganizationUnitGridItem> =
{
  key: 'type',
  name: '',
  cellExpr: (item) =>
    item.type ? (
      <Badge colorScheme="blue">
        {getOrganizationUnitTypeName(item.type)}
      </Badge>
    ) : (
      ''
    ),
  cellProps: { p: 1 },
}

export const OrganizationListDefaultColumns: ColumnsDefinition<OrganizationUnitGridItem> =
  [
    OrganizationListNameColumn,
    OrganizationListSuperiorOrganizationColumn,
    OrganizationListContactPersonColumn,
    OrganizationListCityColumn,
    OrganizationListDescriptionColumn,
    OrganizationListValidColumn,
    OrganizationListTypeColumn,
  ]

export function OrganizationsList<TGridItem extends OrganizationUnitGridItem>({
  organizations,
  sx,
  columnsDefinition = OrganizationListDefaultColumns,
}: {
  organizations: Array<TGridItem>
  sx?: SystemStyleObject
  columnsDefinition?: ColumnsDefinition<TGridItem>
}) {
  const { pageSize, loading } = useContext(organizationListContext)

  return (
    <TableWrapper>
      <Table variant="simple" sx={sx}>
        <Thead>
          <Tr>
            {columnsDefinition &&
              columnsDefinition.map((column) => (
                <Th
                  key={column.key}
                  _empty={{ w: 0, p: 0 }}
                  {...column.headerCellProps}
                >
                  {column.name}
                </Th>
              ))}
          </Tr>
        </Thead>

        <Tbody>
          <TableSkeleton
            rowsCount={pageSize}
            colSpan={columnsDefinition.length}
            isLoaded={!loading}
          >
            <EmptyTablePlaceholder
              text={'Žádné organizace k zobrazení'}
              colSpan={columnsDefinition.length}
            >
              {organizations.map((organization) => {
                return (
                  <Tr key={organization.id}>
                    {columnsDefinition &&
                      columnsDefinition.map((column) => (
                        <Td
                          key={column.key}
                          _empty={{ p: 0 }}
                          {...column.cellProps}
                        >
                          {column.cellExpr(organization) || ''}
                        </Td>
                      ))}
                  </Tr>
                )
              })}
            </EmptyTablePlaceholder>
          </TableSkeleton>
        </Tbody>
      </Table>
    </TableWrapper>
  )
}
